import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/shared/api/client';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Search, Plus, ChevronRight, Download, RotateCcw, Layers } from 'lucide-react';
import { Checkbox } from '@/shared/ui/checkbox';
import { toast } from 'sonner';
import Breadcrumb from '@/shared/components/Breadcrumb';
import ClientDrawer from '@/features/clients/components/ClientDrawer';
import RecherchePdl from '@/shared/components/RecherchePdl';
import RetrouverParPdl from '@/shared/components/RetrouverParPdl';
import { downloadCsv } from '@/shared/utils/download';
import { CLIENT_STATUS_BADGE as STATUS_CONFIG } from '@/shared/constants/statuses';
import { useAuth } from '@/shared/auth/AuthContext';
import useCapacites from '@/shared/auth/useCapacites';
import { CAP } from '@/shared/auth/capacites';
import { EtatRefus, EtatErreur, estUnRefus } from '@/shared/components/EtatEcran';
import useBulkSelection from '@/shared/hooks/useBulkSelection';
import useBulkAction from '@/shared/hooks/useBulkAction';
import BulkEditDialog from '@/shared/components/BulkEditDialog';
import BulkResultDialog from '@/shared/components/BulkResultDialog';
import { changerAgregateur } from '@/shared/api/clients';
import clientBulkActions from '@/features/clients/bulkActions';
import { TAILLE_PAGE, lireBornes } from '@/shared/api/troncature';
import { lireCodePostal, ressembleAUnCodePostal } from '@/shared/api/geo';
import useTypesActifs from '@/shared/hooks/useTypesActifs';
import useRegions from '@/shared/hooks/useRegions';
import BandeauTroncature from '@/shared/components/BandeauTroncature';
import BarrePagination from '@/shared/components/BarrePagination';
import OptionsAgregateurs from '@/shared/components/OptionsAgregateurs';
import useAgregateurs from '@/shared/hooks/useAgregateurs';
import RechercheAdresse from '@/shared/components/RechercheAdresse';

// Un client non attribue n a PAS d agregateur : le champ est vide en base. Le menu
// deroulant, lui, ne sait pas porter une valeur vide — Radix refuse la chaine vide comme
// valeur d option. On passe donc par ce libelle sentinelle a l ecran, et on le retraduit
// en vide au moment d ecrire. L attribution se fait apres la signature du mandat : tant
// qu elle n a pas eu lieu, la liste doit le montrer plutot que d afficher « Survoltage ».
const NON_ATTRIBUE = 'Non attribué';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('statut') || '');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    civilite: 'M.', nom: '', prenom: '', adresse: '', complement_adresse: '',
    code_postal: '', ville: '',
    region: '', departement: '', pdl_prm: '', email: '', telephone: '',
    chauffage_electrique: true, gestionnaire_enedis: true,
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const { possede } = useCapacites();
  const isAdmin = user?.role === 'admin';
  // L'attribution a un agregateur suit la CAPACITE, pas le role : elle se delegue
  // au gestionnaire (ecran « Droits delegues »). La lire dans `isAdmin` rendrait la
  // delegation invisible -- le droit serait ouvert cote serveur et le selecteur
  // resterait absent. ⚠️ Ce n'est pas la protection : la route refuse de toute
  // facon, ceci evite seulement de proposer une action qui repondrait 403.
  const peutAttribuer = possede(CAP.CLIENTS_AGREGATEUR);
  const [installateurFilter, setInstallateurFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [aggregateurFilter, setAggregateurFilter] = useState('');
  const [installateurs, setInstallateurs] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Drawer apercu client (non bloquant)
  const [drawerClientId, setDrawerClientId] = useState(null);
  const [bornes, setBornes] = useState({ total: null, page: 1, taille: TAILLE_PAGE, tronque: false, borne: null });
  const [page, setPage] = useState(1);
  // Les communes du code postal saisi. Un code postal sur six en dessert plusieurs
  // (sept pour 01100) : on les propose au lieu d'en choisir une au hasard.
  const [communesDuCp, setCommunesDuCp] = useState([]);
  const typesActifs = useTypesActifs();
  const regions = useRegions();

  const fetchClients = useCallback(async () => {
    const params = {};
    if (statusFilter) params.statut = statusFilter;
    if (search) params.search = search;
    if (installateurFilter) params.installateur = installateurFilter;
    if (typeFilter) params.type_actif = typeFilter;
    if (aggregateurFilter) params.aggregateur = aggregateurFilter;
    params.page = page;
    params.taille = TAILLE_PAGE;
    try {
      const res = await api.get('/clients', { params });
      setClients(res.data);
      setBornes(lireBornes(res));
      setErreur(null);
    } catch (err) {
      // L'ERREUR EST CONSERVEE (lot I, item I3). Avant, elle partait dans la console et
      // l'ecran annoncait « Aucun client trouve » : un refus de droits et une panne du
      // serveur y ressemblaient trait pour trait a un parc vide.
      console.error(err);
      setClients([]);
      setErreur(err);
    }
    setLoading(false);
  }, [statusFilter, search, installateurFilter, typeFilter, aggregateurFilter, page]);

  // Un changement de filtre ramene a la premiere page : rester en page 7 apres avoir
  // filtre sur deux clients afficherait une liste vide sans raison visible.
  useEffect(() => { setPage(1); }, [statusFilter, search, installateurFilter,
    typeFilter, aggregateurFilter]);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  // LE FILTRE « INSTALLATEUR » N'EST DEMANDE QUE SI ON A LE DROIT DE LE REMPLIR.
  //
  // `/api/users` exige `users:manage`, que seul l'admin possede. Le gestionnaire
  // recevait donc un 403, avale par un `.catch(() => {})` : le menu deroulant restait
  // vide sans que personne ne sache pourquoi, et le refus partait quand meme au
  // serveur. Ce sont les deux lignes du journal nginx du 26/08, a 09:47 puis 09:48.
  //
  // On ne demande plus ce a quoi on n'a pas droit, et on ne propose pas un filtre
  // qu'on ne peut pas remplir : un champ vide sans explication est un defaut, pas une
  // absence de donnees.
  useEffect(() => {
    if (!possede(CAP.USERS_MANAGE)) { setInstallateurs([]); return; }
    api.get('/users')
      .then(r => setInstallateurs(r.data.filter(u => u.role === 'installateur')))
      .catch((e) => {
        // Le refus ne devrait plus arriver ici : s'il arrive, il se voit.
        setInstallateurs([]);
        if (!estUnRefus(e)) toast.error('Liste des installateurs indisponible');
      });
  }, [possede]);

  // --- Actions en masse (admin uniquement) ---
  const bulkSel = useBulkSelection(clients);
  const bulk = useBulkAction(() => { fetchClients(); bulkSel.clear(); });
  const clientLabel = (id) => {
    const c = clients.find(x => x.id === id);
    return c ? `${c.prenom} ${c.nom}` : id;
  };

  // Le catalogue des actions vit dans `features/clients/bulkActions.js` : cet écran ne
  // fait que lui passer la sélection courante.
  const { agregateurs } = useAgregateurs();
  const bulkActions = useMemo(
    () => clientBulkActions(installateurs, agregateurs), [installateurs, agregateurs]);

  const runBulk = async (code, value, def) => {
    setBulkOpen(false);
    await bulk.run(() => def.run(bulkSel.ids, value), {
      alwaysReport: def.alwaysReport,
      success: r => `${r.ok} client${r.ok > 1 ? 's' : ''} traité${r.ok > 1 ? 's' : ''}`,
    });
  };

  // Action rapide : changement agregateur sur une ligne (sans ouvrir la fiche)
  //
  // ⚠️ ELLE NE PASSE PLUS PAR LE CHEMIN EN MASSE. Elle le faisait avec un seul
  // identifiant, ce qui exigeait `bulk:execute` -- accorde au seul admin. Le
  // gestionnaire reussissait donc la MEME action depuis la fiche et prenait un 403
  // depuis la liste. Une action unitaire n'a ni le rapport d'echec partiel, ni le
  // besoin d'atomicite, ni le niveau de droit d'une action en masse.
  const handleRowAggregateurChange = async (clientId, newAgg, ev) => {
    ev?.stopPropagation?.();
    try {
      // Le libelle sentinelle redevient un champ vide : on ecrit une absence
      // d attribution, pas un agregateur qui s appellerait « Non attribue ».
      const valeur = newAgg === NON_ATTRIBUE ? '' : newAgg;
      await changerAgregateur(clientId, valeur);
      toast.success(`Agrégateur → ${newAgg}`);
      fetchClients();
    } catch { toast.error('Erreur lors du changement'); }
  };

  const resetAllFilters = () => {
    setSearch('');
    setStatusFilter('');
    setInstallateurFilter('');
    setTypeFilter('');
    setAggregateurFilter('');
    setSearchParams({});
  };
  const hasAnyFilter = search || statusFilter || installateurFilter || typeFilter || aggregateurFilter;

  // Le code postal remplit la region et le departement, comme CFT Sign le fait deja.
  //
  // La reponse vient du SERVEUR et non d'une table locale : voir shared/api/geo.js pour
  // la raison. Elle n'ecrase que ce que l'utilisateur n'a pas rempli, et la ville n'est
  // proposee que si le code postal ne dessert qu'une seule commune — sinon on afficherait
  // une commune fausse six fois sur sept.
  const majCodePostal = async (codePostal) => {
    setNewClient(c => ({ ...c, code_postal: codePostal }));
    if (!ressembleAUnCodePostal(codePostal)) {
      setCommunesDuCp([]);
      return;
    }
    try {
      const geo = await lireCodePostal(codePostal);
      setCommunesDuCp(geo.communes || []);
      setNewClient(c => ({
        ...c,
        region: c.region || geo.region,
        departement: c.departement || geo.departement,
        ville: c.ville || (geo.communes?.length === 1 ? geo.communes[0].nom : ''),
      }));
    } catch {
      // 404 : le code postal n'existe pas. On ne crie pas pendant la frappe — le
      // serveur refusera l'enregistrement et dira pourquoi, une fois, au bon moment.
      setCommunesDuCp([]);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/clients', newClient);
      setDialogOpen(false);
      setNewClient({ civilite: 'M.', nom: '', prenom: '', adresse: '', code_postal: '', ville: '',
        region: '', departement: '', pdl_prm: '', email: '', telephone: '',
        chauffage_electrique: true, gestionnaire_enedis: true });
      setCommunesDuCp([]);
      navigate(`/clients/${res.data.id}`);
    } catch (err) {
      // Le refus d'une adresse fausse DOIT se voir. La version precedente se contentait
      // d'un `console.error` : la boite restait ouverte, le bouton avait l'air de ne
      // rien faire, et l'utilisateur ne pouvait pas savoir que son code postal etait en
      // cause. Le serveur dit quoi corriger, encore faut-il le lui montrer.
      toast.error(err?.response?.data?.detail || "Impossible de creer le client");
    }
  };

  const handleExport = async () => {
    try {
      await downloadCsv('/export/clients/csv');
    } catch (err) { console.error(err); }
  };

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="clients-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Breadcrumb items={[{ label: 'Clients' }]} />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Clients
          </h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="clients-results-count">
            {clients.length} client{clients.length > 1 ? 's' : ''}{hasAnyFilter ? ' (filtrés)' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && bulkSel.count > 0 && (
            <Button size="sm" onClick={() => setBulkOpen(true)} data-testid="open-bulk-btn">
              <Layers className="w-4 h-4 mr-1" /> Édition groupée ({bulkSel.count})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-clients-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-1" /> Nouveau client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nouveau client</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Civilite</Label>
                    <Select value={newClient.civilite} onValueChange={v => setNewClient(c => ({ ...c, civilite: v }))}>
                      <SelectTrigger data-testid="new-client-civilite"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M.">M.</SelectItem>
                        <SelectItem value="Mme">Mme</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nom *</Label>
                    <Input required value={newClient.nom} onChange={e => setNewClient(c => ({ ...c, nom: e.target.value }))} data-testid="new-client-nom" />
                  </div>
                </div>
                <div>
                  <Label>Prenom *</Label>
                  <Input required value={newClient.prenom} onChange={e => setNewClient(c => ({ ...c, prenom: e.target.value }))} data-testid="new-client-prenom" />
                </div>
                <div>
                  <Label>Adresse</Label>
                  {/* Un choix pose adresse et ville, puis passe le code postal par
                      `majCodePostal` -- le MEME chemin que la saisie manuelle du champ
                      code postal juste en dessous. C'est lui qui remplit region et
                      departement depuis le serveur ; les recopier ici ferait un second
                      chemin, et les deux divergeraient au premier correctif. */}
                  <RechercheAdresse
                    valeur={newClient.adresse}
                    onChange={v => setNewClient(c => ({ ...c, adresse: v }))}
                    onChoisir={p => {
                      setNewClient(c => ({ ...c, adresse: p.adresse, ville: p.ville }));
                      majCodePostal(p.code_postal);
                    }}
                    testid="new-client-adresse" />
                </div>
                <div>
                  <Label>Complement d&apos;adresse</Label>
                  <Input value={newClient.complement_adresse || ''}
                    onChange={e => setNewClient(c => ({ ...c, complement_adresse: e.target.value }))}
                    placeholder="Batiment, escalier, etage"
                    data-testid="new-client-complement-adresse" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Code postal</Label>
                    <Input value={newClient.code_postal} onChange={e => majCodePostal(e.target.value)} data-testid="new-client-cp" />
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input list="communes-du-code-postal" value={newClient.ville} onChange={e => setNewClient(c => ({ ...c, ville: e.target.value }))} data-testid="new-client-ville" />
                    <datalist id="communes-du-code-postal">
                      {communesDuCp.map(commune => (
                        <option key={commune.code_insee} value={commune.nom} />
                      ))}
                    </datalist>
                    {communesDuCp.length > 1 && (
                      <p className="text-[11px] text-slate-500 mt-1" data-testid="plusieurs-communes">
                        {communesDuCp.length} communes pour ce code postal : choisissez la bonne.
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Region</Label>
                    <Select value={newClient.region || ''} onValueChange={v => setNewClient(c => ({ ...c, region: v }))}>
                      <SelectTrigger data-testid="new-client-region"><SelectValue placeholder="Selectionner une region" /></SelectTrigger>
                      <SelectContent>
                        {regions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Departement</Label>
                    <Input value={newClient.departement} onChange={e => setNewClient(c => ({ ...c, departement: e.target.value }))} data-testid="new-client-dept" />
                  </div>
                </div>
                <div>
                  <Label>PDL/PRM</Label>
                  <Input value={newClient.pdl_prm} onChange={e => setNewClient(c => ({ ...c, pdl_prm: e.target.value }))} data-testid="new-client-pdl" />
                  {/* Le bouton manquait ICI, et seulement ici : le wizard du poseur et
                      le formulaire QR l'ont depuis le lot O. Un commercial qui saisit un
                      client au telephone n'avait donc aucun moyen de verifier le numero
                      qu'on lui dictait. `authentifie` fait passer l'appel par la route
                      du CRM, plafonnee par COMPTE et non par adresse. */}
                  <div className="mt-1.5">
                    <RecherchePdl client={newClient} valeurActuelle={newClient.pdl_prm}
                      authentifie
                      onTrouve={pdl => setNewClient(c => ({ ...c, pdl_prm: pdl }))}
                      onCorrigerChamps={champs => setNewClient(c => ({ ...c, ...champs }))}
                      testid="new-client-pdl-recherche" />
                  </div>
                  {/* LE SENS RETOUR : le commercial a le numero, dicte au telephone par
                      le client. C'est celui-la qu'il faut verifier -- un chiffre mal
                      entendu designe le compteur d'un voisin, et ce numero sert de cle
                      de deduplication des fiches. */}
                  <div className="mt-1.5">
                    <RetrouverParPdl client={newClient} authentifie
                      onCorrigerChamps={champs => setNewClient(c => ({ ...c, ...champs }))}
                      testid="new-client-pdl-retrouver" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Email *</Label>
                    {/* Obligatoire depuis le 03/09. Le serveur refuse aussi, et c'est LUI
                        qui garde la porte : `required` ne protege que le clavier, une
                        requete tapee a la main passerait a cote. Voir
                        `backend/app/core/email_saisie.py`. La phrase ci-dessous dit ce
                        qu'on perd sans adresse — un asterisque seul dit qu'il faut
                        remplir, jamais pourquoi. */}
                    <Input required type="email" value={newClient.email} onChange={e => setNewClient(c => ({ ...c, email: e.target.value }))} data-testid="new-client-email" />
                    <p className="text-[11px] text-slate-500 mt-1" data-testid="new-client-email-obligatoire">
                      Le mandat et l&apos;attestation partent a cette adresse.
                    </p>
                  </div>
                  <div>
                    <Label>Telephone</Label>
                    <Input value={newClient.telephone} onChange={e => setNewClient(c => ({ ...c, telephone: e.target.value }))} data-testid="new-client-tel" />
                  </div>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-new-client-btn">
                  Creer le client
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Rechercher un client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-clients-input"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['', 'prospect', 'mandat_envoye', 'signe', 'actif', 'resilie'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === s
                  ? 'bg-neutral-900 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`filter-status-${s || 'all'}`}
            >
              {s ? STATUS_CONFIG[s]?.label : 'Tous'}
            </button>
          ))}
        </div>
        {/* Le filtre n'est propose qu'a qui peut le remplir : sa liste vient de
            `/api/users`, refuse a tout role sans `users:manage`. Un menu deroulant
            vide n'annonce rien, il laisse croire qu'aucun installateur n'existe. */}
        {possede(CAP.USERS_MANAGE) && (
        <Select value={installateurFilter || 'all'} onValueChange={v => setInstallateurFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="filter-installateur-clients">
            <SelectValue placeholder="Installateur" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous installateurs</SelectItem>
            {installateurs.map(i => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}
          </SelectContent>
        </Select>
        )}
        <Select value={typeFilter || 'all'} onValueChange={v => setTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-[150px] text-sm" data-testid="filter-type-clients">
            <SelectValue placeholder="Type actif" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            {typesActifs.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={aggregateurFilter || 'all'} onValueChange={v => setAggregateurFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-[160px] text-sm" data-testid="filter-aggregateur-clients">
            <SelectValue placeholder="Agrégateur" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous agrégateurs</SelectItem>
            <OptionsAgregateurs />
          </SelectContent>
        </Select>
        {hasAnyFilter && (
          <Button variant="ghost" size="sm" onClick={resetAllFilters} className="ml-auto" data-testid="reset-filters-clients-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Réinitialiser les filtres
          </Button>
        )}
      </div>

      {/* Table */}
      <BandeauTroncature etat={bornes} quoi="clients" />

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
            </div>
          ) : erreur ? (
            /* Trois messages distincts : un refus n'est pas une panne, et une panne
               n'est pas un parc vide. */
            estUnRefus(erreur) ? <EtatRefus quoi="la liste des clients" />
                               : <EtatErreur onReessayer={fetchClients} />
          ) : clients.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <p>Aucun client trouve</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    {isAdmin && (
                      <Checkbox checked={bulkSel.allSelected} onCheckedChange={bulkSel.toggleAll} data-testid="select-all-clients" />
                    )}
                  </TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>PDL/PRM</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Agrégateur</TableHead>
                  <TableHead className="text-center">Actifs</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map(client => (
                  <TableRow
                    key={client.id}
                    className={`cursor-pointer hover:bg-slate-50 ${drawerClientId === client.id ? 'bg-slate-50' : ''}`}
                    onClick={() => setDrawerClientId(client.id)}
                    data-testid={`client-row-${client.id}`}
                  >
                    <TableCell onClick={e => e.stopPropagation()}>
                      {isAdmin && (
                        <Checkbox checked={bulkSel.isSelected(client.id)} onCheckedChange={() => bulkSel.toggle(client.id)} data-testid={`select-client-${client.id}`} />
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{client.civilite} {client.prenom} {client.nom}</p>
                        <p className="text-xs text-slate-500">{client.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{client.ville}</TableCell>
                    <TableCell className="text-sm font-mono text-slate-500">{client.pdl_prm || '-'}</TableCell>
                    <TableCell>
                      <span className={`status-badge ${STATUS_CONFIG[client.statut]?.class || ''}`}>
                        {STATUS_CONFIG[client.statut]?.label || client.statut}
                      </span>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {/* Un client sans agregateur s affichait « Survoltage » : le repli
                          d affichage racontait une attribution qui n avait pas eu lieu, et
                          la liste devenait le seul endroit ou on ne pouvait pas voir qu il
                          restait des clients a attribuer. L attribution se fait APRES la
                          signature ; tant qu elle n a pas eu lieu, l ecran le dit. */}
                      {!peutAttribuer ? (
                        <span className="text-[11px] text-neutral-600">{client.aggregateur || 'Non attribué'}</span>
                      ) : (
                      <Select
                        value={client.aggregateur || NON_ATTRIBUE}
                        onValueChange={v => handleRowAggregateurChange(client.id, v)}
                      >
                        <SelectTrigger
                          className={`h-7 text-[11px] border-0 w-[120px] focus:ring-1 focus:ring-neutral-300 ${
                            client.aggregateur === 'Survoltage' ? 'bg-violet-50 text-violet-700' :
                            client.aggregateur === 'Symphonics' ? 'bg-cyan-50 text-cyan-700' :
                            !client.aggregateur ? 'bg-amber-50 text-amber-700' :
                            'bg-neutral-50 text-neutral-600'
                          }`}
                          data-testid={`row-aggregateur-${client.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NON_ATTRIBUE}>{NON_ATTRIBUE}</SelectItem>
                          <OptionsAgregateurs />
                        </SelectContent>
                      </Select>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{client.asset_count || 0}</Badge>
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <BarrePagination etat={bornes} onPage={setPage} quoi="clients" busy={loading} />

      {/* Actions en masse — admin uniquement */}
      <BulkEditDialog
        open={bulkOpen}
        title="Édition groupée"
        noun="client"
        count={bulkSel.count}
        actions={bulkActions}
        selectedItems={bulkSel.selectedItems}
        busy={bulk.busy}
        onCancel={() => setBulkOpen(false)}
        onSubmit={runBulk}
      />

      <BulkResultDialog
        report={bulk.report} onClose={bulk.clearReport}
        title="Résultat de l'action en masse" resolveLabel={clientLabel}
      />

      {/* Drawer apercu client (non bloquant) */}
      <ClientDrawer clientId={drawerClientId} onClose={() => setDrawerClientId(null)} />
    </div>
  );
}
